// src/pages/wages/WagePaymentHistory.tsx
//
// Enhanced Payment History — shows a detailed breakdown of each payment
// including regular/OT hours, rates, and payments, with a summary footer
// and improved stat cards.

import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import type { IFilter } from "../../components/common/CommonFilter";
import { ThrowError } from "../../api/callApi";
import {
  Eye,
  IndianRupee,
  MoreHorizontal,
  Receipt,
  Users,
  Clock,
  CalendarRange,
  TrendingUp,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import CommonFilter from "../../components/common/CommonFilter";
import CommonTable, {
  type TableColumn,
} from "../../components/common/CommonTable";
import TablePagination from "../../components/common/TablePagination";
import { RoutesLink } from "../../utility/RouteLinks";
import type { IPageInfo } from "./Wages";

// ─── Types ────────────────────────────────────────────────────────────────

interface IPaymentRecord {
  id: string;
  labor: { id: string; name: string };
  site: { id: string; name: string };
  paidOn: string; // ISO date
  periodStart: string;
  periodEnd: string;
  daysWorked: number;
  dailyRate: number;
  regularHours: number; // total regular hours in the period
  otHours: number; // total overtime hours
  regularPayment: number;
  otPayment: number;
  advance: number;
  netPaid: number;
  paidBy: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

const inr = (value: number) =>
  `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  trend?: string;
}> = ({ label, value, icon, trend }) => (
  <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 flex items-center gap-3">
    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
        {label}
      </p>
      <div className="flex items-baseline gap-2">
        <p className="text-lg font-semibold text-gray-800 truncate">{value}</p>
        {trend && (
          <span className="text-xs text-emerald-600 font-medium">{trend}</span>
        )}
      </div>
    </div>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────

const WagePaymentHistory: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [records, setRecords] = useState<IPaymentRecord[]>([]);
  const [fromDate, setFromDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [filter, setFilter] = useState<IFilter & { siteId?: string }>({
    search: "",
    status: "",
    siteId: "",
  });
  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      // TODO: replace with real API call
      // const res = await WageService.getPaymentHistory({ fromDate, toDate, ...filter });
      const mockData: IPaymentRecord[] = [
        {
          id: "p1",
          labor: { id: "1", name: "Ramesh Kumar" },
          site: { id: "s1", name: "Site 4 — Roofing" },
          paidOn: "2026-08-08",
          periodStart: "2026-07-25",
          periodEnd: "2026-08-08",
          daysWorked: 15,
          dailyRate: 650,
          regularHours: 120,
          otHours: 8,
          regularPayment: 7800,
          otPayment: 975,
          advance: 2000,
          netPaid: 6775,
          paidBy: "Aarav Sharma",
        },
        {
          id: "p2",
          labor: { id: "2", name: "Sunita Devi" },
          site: { id: "s2", name: "Site 2 — Block A" },
          paidOn: "2026-08-11",
          periodStart: "2026-08-01",
          periodEnd: "2026-08-10",
          daysWorked: 10,
          dailyRate: 600,
          regularHours: 80,
          otHours: 2,
          regularPayment: 4800,
          otPayment: 180,
          advance: 0,
          netPaid: 4980,
          paidBy: "Aarav Sharma",
        },
        {
          id: "p3",
          labor: { id: "3", name: "Arvind Yadav" },
          site: { id: "s1", name: "Site 4 — Roofing" },
          paidOn: "2026-08-05",
          periodStart: "2026-07-06",
          periodEnd: "2026-08-05",
          daysWorked: 30,
          dailyRate: 650,
          regularHours: 240,
          otHours: 12,
          regularPayment: 15600,
          otPayment: 1170,
          advance: 5000,
          netPaid: 11770,
          paidBy: "Priya Singh",
        },
        {
          id: "p4",
          labor: { id: "4", name: "Priya Sharma" },
          site: { id: "s4", name: "Site 3 — Interior" },
          paidOn: "2026-08-15",
          periodStart: "2026-08-01",
          periodEnd: "2026-08-14",
          daysWorked: 12,
          dailyRate: 750,
          regularHours: 96,
          otHours: 6,
          regularPayment: 7200,
          otPayment: 675,
          advance: 1000,
          netPaid: 6875,
          paidBy: "Aarav Sharma",
        },
      ];
      setRecords(mockData);
      setPaginationData((prev) => ({
        ...prev,
        total: mockData.length,
        totalPages: 1,
      }));
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationData.currentPage,
    paginationData.pageSize,
    filter,
    fromDate,
    toDate,
  ]);

  // ─── Aggregations ──────────────────────────────────────────────────────

  const totalPaidOut = records.reduce((sum, r) => sum + r.netPaid, 0);
  const totalWorkers = new Set(records.map((r) => r.labor.id)).size;
  const totalRegularPay = records.reduce((sum, r) => sum + r.regularPayment, 0);
  const totalOTPay = records.reduce((sum, r) => sum + r.otPayment, 0);
  const totalDays = records.reduce((sum, r) => sum + r.daysWorked, 0);

  // ─── Table Columns ────────────────────────────────────────────────────

  const columns: TableColumn<IPaymentRecord>[] = [
    {
      key: "labor",
      header: "Worker",
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
            {item.labor.name.charAt(0).toUpperCase()}
          </div>
          <p className="font-semibold text-gray-700 truncate">
            {item.labor.name}
          </p>
        </div>
      ),
    },
    {
      key: "site",
      header: "Site",
      render: (item) => <span className="text-sm">{item.site.name}</span>,
    },
    {
      key: "period",
      header: "Period",
      render: (item) => (
        <span className="text-sm text-slate-600">
          {item.periodStart} → {item.periodEnd}
        </span>
      ),
    },
    {
      key: "daysWorked",
      header: "Days",
      render: (item) => <span className="font-medium">{item.daysWorked}</span>,
    },
    {
      key: "dailyRate",
      header: "Rate/day",
      render: (item) => (
        <span className="text-sm text-slate-600">{inr(item.dailyRate)}</span>
      ),
    },
    {
      key: "hours",
      header: "Hours",
      render: (item) => (
        <div className="flex flex-col text-xs">
          <span className="text-slate-600">Reg: {item.regularHours}h</span>
          <span className="text-amber-600">OT: {item.otHours}h</span>
        </div>
      ),
    },
    {
      key: "payments",
      header: "Payments",
      render: (item) => (
        <div className="flex flex-col text-xs">
          <span className="text-slate-600">
            Reg: {inr(item.regularPayment)}
          </span>
          <span className="text-amber-600">OT: {inr(item.otPayment)}</span>
        </div>
      ),
    },
    {
      key: "advance",
      header: "Advance",
      render: (item) => (
        <span className="text-sm text-slate-500">
          {item.advance > 0 ? inr(item.advance) : "—"}
        </span>
      ),
    },
    {
      key: "netPaid",
      header: "Net Paid",
      render: (item) => (
        <span className="font-bold text-primary text-base">
          {inr(item.netPaid)}
        </span>
      ),
    },
    {
      key: "paidBy",
      header: "Processed By",
      render: (item) => (
        <span className="text-sm text-slate-500">{item.paidBy}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() =>
                navigate(`${RoutesLink.laborDetail}/${item.labor.id}`)
              }
              className="cursor-pointer"
            >
              <Eye className="h-4 w-4 mr-2" /> View Worker
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer">
              <Receipt className="h-4 w-4 mr-2" /> View Receipt
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <MainHeading title="Payment History" />
        <div className="flex items-center gap-2 bg-white border border-slate-200/70 rounded-xl p-1.5 shadow-sm">
          <div className="flex items-center gap-1">
            <CalendarRange size={16} className="text-slate-400 ml-1" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border-0 bg-transparent px-2 py-1 text-sm focus:ring-0 focus:outline-none w-36"
            />
            <span className="text-slate-300 text-sm">→</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border-0 bg-transparent px-2 py-1 text-sm focus:ring-0 focus:outline-none w-36"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          label="Workers Paid"
          value={totalWorkers}
          icon={<Users size={18} className="text-primary" />}
        />
        <StatCard
          label="Total Paid Out"
          value={inr(totalPaidOut)}
          icon={<IndianRupee size={18} className="text-primary" />}
        />
        <StatCard
          label="Total Days Worked"
          value={totalDays}
          icon={<Clock size={18} className="text-primary" />}
        />
        <StatCard
          label="OT Payment"
          value={inr(totalOTPay)}
          icon={<TrendingUp size={18} className="text-amber-500" />}
          trend={`${totalOTPay > 0 ? `${Math.round((totalOTPay / totalPaidOut) * 100)}%` : ""}`}
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-100">
          <CommonFilter
            searchValue={filter.search || ""}
            onSearchChange={(v) => setFilter((f) => ({ ...f, search: v }))}
            value={filter}
            filterOptions={[
              { label: "All Sites", value: "" },
              { label: "Site 4 — Roofing", value: "s1" },
              { label: "Site 2 — Block A", value: "s2" },
            ]}
            filterName="siteId"
            filterPlaceholder="Filter by Site"
            onChange={(v, name) => setFilter((f) => ({ ...f, [name]: v }))}
          />
        </div>

        <CommonTable
          data={records}
          columns={columns}
          loading={isLoading}
          stickyColumns={1}
        />

        {/* Footer Summary */}
        {records.length > 0 && (
          <div className="border-t border-slate-100 bg-slate-50/80 px-4 py-3 flex flex-wrap items-center justify-between gap-4 text-sm">
            <div className="flex flex-wrap items-center gap-4 text-slate-600">
              <span>
                Total Payments: <strong>{records.length}</strong>
              </span>
              <span>
                Total Regular: <strong>{inr(totalRegularPay)}</strong>
              </span>
              <span>
                Total OT:{" "}
                <strong className="text-amber-600">{inr(totalOTPay)}</strong>
              </span>
              <span>
                Total Advance:{" "}
                <strong>
                  {inr(records.reduce((s, r) => s + r.advance, 0))}
                </strong>
              </span>
            </div>
            <div className="text-primary font-semibold text-base">
              Grand Total: {inr(totalPaidOut)}
            </div>
          </div>
        )}

        <div className="p-4 border-t border-slate-100">
          <TablePagination
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            handleChangePageSize={(value) =>
              setPaginationData((prev) => ({
                ...prev,
                pageSize: value,
                currentPage: 1,
              }))
            }
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default WagePaymentHistory;
